import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  SlideOver,
  Table,
  Text,
  useToast,
} from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import type { Member } from '../../store/api/portalApi';
import { useGetAgentsQuery } from '../../store/api/agentsApi';
import type { Agent } from '../../store/api/agentsApi';
import {
  useAddTeamMemberMutation,
  useCreateTeamMutation,
  useDeleteTeamMutation,
  useGetTeamQuery,
  useGetTeamsQuery,
  usePutTeamPermissionsMutation,
  useRemoveTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useUpdateTeamMutation,
} from '../../store/api/teamsApi';
import type { Team, TeamMemberRow, TeamRole, TeamStatement } from '../../store/api/teamsApi';
import {
  AGENT_ACCESS_LABELS,
  AGENT_ACCESS_OPTIONS,
  agentAccessLevel,
  withAgentAccess,
} from './teamAccess';
import type { AgentAccessLevel } from './teamAccess';
import { MemberRolesPicker, TeamRolesSection } from './TeamRolesSection';

function errorMessage(error: unknown): string {
  const data = (error as { data?: { message?: unknown } })?.data;
  return typeof data?.message === 'string' ? data.message : 'Something went wrong. Please try again.';
}

function memberName(member: Member): string {
  const full = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  return full || member.email || member.userId;
}

/* ---------- Styles ---------- */

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Muted = styled.span`
  color: var(--text-muted);
  font-size: 0.8125rem;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Select = styled.select`
  padding: 0.375rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text);
  font-size: 0.8125rem;
`;

const Note = styled.p`
  margin: 0;
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--surface-muted, rgba(0, 0, 0, 0.03));
  color: var(--text-muted);
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--error);
  font-size: 0.8125rem;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 240px;
  overflow-y: auto;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

/* ---------- Create dialog ---------- */

interface CreateTeamDialogProps {
  orgId: string;
  open: boolean;
  members: Member[];
  onClose: () => void;
  onCreated: (teamId: string) => void;
}

export const CreateTeamDialog: React.FC<CreateTeamDialogProps> = ({
  orgId,
  open,
  members,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createTeam, { isLoading }] = useCreateTeamMutation();

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setMemberIds([]);
      setError(null);
    }
  }, [open]);

  const toggle = (id: string) =>
    setMemberIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  const submit = async () => {
    if (!name.trim()) {
      setError('Team name is required');
      return;
    }
    try {
      const team = await createTeam({
        orgId,
        name: name.trim(),
        description: description.trim() || undefined,
        memberIds,
      }).unwrap();
      onCreated(team.id);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Create team">
      <Form>
        <Input
          label="Team name"
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          placeholder="e.g. Support"
          fullWidth
        />
        <Input
          label="Description"
          value={description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
          placeholder="What this team works on"
          fullWidth
        />
        <div>
          <SectionTitle>Members</SectionTitle>
          <Muted>You become the team owner. Pick anyone else to add.</Muted>
        </div>
        <CheckList>
          {members.map((m) => (
            <CheckRow key={m.id}>
              <input
                type="checkbox"
                checked={memberIds.includes(m.id)}
                onChange={() => toggle(m.id)}
                aria-label={`Add ${memberName(m)}`}
              />
              {memberName(m)}
              <Muted>{m.email}</Muted>
            </CheckRow>
          ))}
        </CheckList>
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <Actions>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={isLoading}>
            {isLoading ? 'Creating…' : 'Create team'}
          </Button>
        </Actions>
      </Form>
    </Modal>
  );
};

/* ---------- Drawer ---------- */

interface TeamDrawerProps {
  orgId: string;
  teamId: string | null;
  members: Member[];
  onClose: () => void;
}

export const TeamDrawer: React.FC<TeamDrawerProps> = ({ orgId, teamId, members, onClose }) => {
  const { show } = useToast();
  const { data: team, isLoading } = useGetTeamQuery(
    { orgId, teamId: teamId || '' },
    { skip: !teamId },
  );
  const { data: agents = [] } = useGetAgentsQuery({ orgId }, { skip: !teamId });
  const [addTeamMember] = useAddTeamMemberMutation();
  const [updateTeamMember] = useUpdateTeamMemberMutation();
  const [removeTeamMember] = useRemoveTeamMemberMutation();
  const [updateTeam, { isLoading: savingDetails }] = useUpdateTeamMutation();
  const [deleteTeam] = useDeleteTeamMutation();
  const [putPermissions, { isLoading: savingAccess }] = usePutTeamPermissionsMutation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [addMemberId, setAddMemberId] = useState('');
  const [statements, setStatements] = useState<TeamStatement[]>([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (team) {
      setName(team.name);
      setDescription(team.description || '');
      setStatements(team.statements || []);
      setDirty(false);
      setError(null);
    }
  }, [team]);

  const orgAgents = useMemo(
    () => agents.filter((a: Agent) => a.scope === 'organisation' && !a.readOnly),
    [agents],
  );

  const inTeam = new Set((team?.members || []).map((m) => m.memberId));
  const addable = members.filter((m) => !inTeam.has(m.id));
  const isOrgTeam = !!team?.isDefault;
  const teamRoles = team?.roles || [];

  const run = async (action: () => Promise<unknown>, success?: string) => {
    setError(null);
    try {
      await action();
      if (success) show({ message: success, type: 'success' });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const setAccess = (agentId: string, level: AgentAccessLevel) => {
    setStatements((current) => withAgentAccess(current, agentId, level));
    setDirty(true);
  };

  const saveAccess = () =>
    run(
      () => putPermissions({ orgId, teamId: teamId!, statements }).unwrap(),
      'Agent access saved',
    ).then(() => setDirty(false));

  const remove = () => {
    if (!team || !window.confirm(`Delete team "${team.name}"?`)) return;
    run(async () => {
      await deleteTeam({ orgId, teamId: team.id }).unwrap();
      onClose();
    }, 'Team deleted');
  };

  return (
    <SlideOver isOpen={!!teamId} onClose={onClose} title={team?.name || 'Team'} width="lg">
      {isLoading || !team ? (
        <Muted>Loading team…</Muted>
      ) : (
        <>
          {error && <ErrorText role="alert">{error}</ErrorText>}

          <Section aria-label="Details">
            <SectionTitle>Details</SectionTitle>
            {isOrgTeam ? (
              <Note>
                Everyone in the organisation is in the Org team, and org owners are its owners.
                Membership follows the organisation, so it cannot be edited here.
              </Note>
            ) : (
              <>
                <Input
                  label="Team name"
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                  fullWidth
                />
                <Input
                  label="Description"
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDescription(e.target.value)
                  }
                  fullWidth
                />
                <Actions>
                  <Button variant="ghost" onClick={remove} style={{ color: 'var(--error)' }}>
                    Delete team
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={savingDetails}
                    onClick={() =>
                      run(
                        () =>
                          updateTeam({ orgId, teamId: team.id, name, description }).unwrap(),
                        'Team updated',
                      )
                    }
                  >
                    Save details
                  </Button>
                </Actions>
              </>
            )}
          </Section>

          <Section aria-label="Members">
            <SectionTitle>Members ({team.members.length})</SectionTitle>
            {team.members.map((m: TeamMemberRow) => (
              <Row key={m.memberId}>
                <RowText>
                  <Text variant="body2" style={{ fontWeight: 500 }}>
                    {m.displayName || m.email}
                  </Text>
                  <Muted>{m.email}</Muted>
                </RowText>
                <RowActions>
                  {teamRoles.length > 0 && (
                    <MemberRolesPicker
                      orgId={orgId}
                      teamId={team.id}
                      memberId={m.memberId}
                      label={m.displayName || m.email || m.userId}
                      roles={teamRoles}
                      held={m.roleIds || []}
                      run={run}
                    />
                  )}
                  {isOrgTeam ? (
                    <Badge variant={m.teamRole === 'owner' ? 'primary' : 'neutral'} size="sm">
                      {m.teamRole}
                    </Badge>
                  ) : (
                    <>
                      <Select
                        value={m.teamRole}
                        aria-label={`Team role for ${m.displayName || m.email}`}
                        onChange={(e) =>
                          run(() =>
                            updateTeamMember({
                              orgId,
                              teamId: team.id,
                              memberId: m.memberId,
                              teamRole: e.target.value as TeamRole,
                            }).unwrap(),
                          )
                        }
                      >
                        <option value="owner">Owner</option>
                        <option value="member">Member</option>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${m.displayName || m.email} from team`}
                        onClick={() =>
                          run(() =>
                            removeTeamMember({
                              orgId,
                              teamId: team.id,
                              memberId: m.memberId,
                            }).unwrap(),
                          )
                        }
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </RowActions>
              </Row>
            ))}
            {!isOrgTeam && addable.length > 0 && (
              <Row>
                <Select
                  value={addMemberId}
                  aria-label="Member to add"
                  onChange={(e) => setAddMemberId(e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="">Add a member…</option>
                  {addable.map((m) => (
                    <option key={m.id} value={m.id}>
                      {memberName(m)}
                    </option>
                  ))}
                </Select>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!addMemberId}
                  onClick={() =>
                    run(async () => {
                      await addTeamMember({
                        orgId,
                        teamId: team.id,
                        memberId: addMemberId,
                      }).unwrap();
                      setAddMemberId('');
                    })
                  }
                >
                  Add
                </Button>
              </Row>
            )}
          </Section>

          <TeamRolesSection
            orgId={orgId}
            teamId={team.id}
            roles={teamRoles}
            agents={orgAgents}
            run={run}
          />

          <Section aria-label="Agent access">
            <SectionTitle>Agent access for the whole team</SectionTitle>
            <Note>
              {isOrgTeam
                ? 'The Org team gives everyone access to each organisation agent. Set an agent to "Not set" to keep it for specific teams or roles only.'
                : 'Allow adds access for every member of this team. Deny removes access for its members even when another team or role allows it.'}
            </Note>
            {orgAgents.length === 0 ? (
              <EmptyState
                title="No organisation agents yet"
                description="Agents your organisation builds appear here."
              />
            ) : (
              orgAgents.map((agent: Agent) => {
                const level = agentAccessLevel(statements, agent.id);
                return (
                  <Row key={agent.id}>
                    <RowText>
                      <Text variant="body2" style={{ fontWeight: 500 }}>
                        {agent.name || agent.agentName}
                      </Text>
                      <Muted>{agent.agentName}</Muted>
                    </RowText>
                    <Select
                      value={level}
                      aria-label={`Access to ${agent.name || agent.agentName}`}
                      onChange={(e) => setAccess(agent.id, e.target.value as AgentAccessLevel)}
                    >
                      {level === 'custom' && (
                        <option value="custom">{AGENT_ACCESS_LABELS.custom}</option>
                      )}
                      {AGENT_ACCESS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </Row>
                );
              })
            )}
            <Actions>
              <Button variant="primary" disabled={!dirty || savingAccess} onClick={saveAccess}>
                {savingAccess ? 'Saving…' : 'Save agent access'}
              </Button>
            </Actions>
          </Section>
        </>
      )}
    </SlideOver>
  );
};

/* ---------- Tab ---------- */

interface TeamsTabProps {
  orgId: string;
  members: Member[];
}

const TeamsTab: React.FC<TeamsTabProps> = ({ orgId, members }) => {
  const { data: teams = [], isLoading, error } = useGetTeamsQuery(orgId);
  const [createOpen, setCreateOpen] = useState(false);
  const [openTeamId, setOpenTeamId] = useState<string | null>(null);

  const columns: Column<Team>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Team',
        render: (_v: unknown, row: Team) => (
          <NameCell>
            <Text variant="body2" style={{ fontWeight: 500 }}>
              {row.name}
            </Text>
            {row.isDefault && (
              <Badge variant="info" size="sm">
                Everyone
              </Badge>
            )}
          </NameCell>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        render: (_v: unknown, row: Team) => (
          <Muted>
            {row.description ||
              (row.isDefault ? 'Every member of the organisation' : '—')}
          </Muted>
        ),
      },
      {
        key: 'memberCount',
        header: 'Members',
        render: (_v: unknown, row: Team) => <>{row.memberCount}</>,
      },
      {
        key: 'actions',
        header: '',
        render: (_v: unknown, row: Team) => (
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Open ${row.name}`}
            onClick={() => setOpenTeamId(row.id)}
          >
            Open
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Header>
        <HeaderText>
          <h3 style={{ margin: 0 }}>Teams</h3>
          <Muted>
            Teams and their roles decide who can use which agents. A deny on the org role, a
            team or a team role wins.
          </Muted>
        </HeaderText>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          + Create team
        </Button>
      </Header>

      {error ? (
        <ErrorText role="alert">Failed to load teams. Please try again.</ErrorText>
      ) : isLoading ? (
        <Muted>Loading teams…</Muted>
      ) : (
        <Table columns={columns} data={teams} rowKey="id" />
      )}

      <CreateTeamDialog
        orgId={orgId}
        open={createOpen}
        members={members}
        onClose={() => setCreateOpen(false)}
        onCreated={(teamId) => {
          setCreateOpen(false);
          setOpenTeamId(teamId);
        }}
      />
      <TeamDrawer
        orgId={orgId}
        teamId={openTeamId}
        members={members}
        onClose={() => setOpenTeamId(null)}
      />
    </>
  );
};

export default TeamsTab;
